import { captureMessage } from '@sentry/react';

import { readAttribution } from 'utils/attribution';
import type { ShipmentType } from 'domain/shipment/shipment.constants';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Evento propio, gestionado desde el contenedor GTM. */
const EVENT_NAME = 'transport_request_published';

/**
 * Define window.gtag una sola vez y con la forma correcta.
 *
 * gtag.js lee el objeto `arguments`, no un Array. Empujar un Array —como hacía
 * GoogleAnalytics.tsx— hace que el comando se descarte en silencio, que es el peor
 * modo de fallar que tiene una herramienta de medición.
 */
export function ensureGtag(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') return;
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
}

/**
 * Saca el id del envío de la respuesta del backend.
 *
 * Los dos caminos de publicación devuelven formas distintas, verificado contra
 * nexvia-api-web: POST /shipments devuelve el shipment entero (`id`), y
 * POST /shipments/drafts/:id/publish devuelve `{ shipmentId, status, ... }`.
 * Se contemplan además las respuestas ya desenvueltas, como hace
 * useNewShipmentController al crear borradores.
 */
export function resolveShipmentId(response: unknown): string | undefined {
  const res = response as { data?: Record<string, unknown> } & Record<string, unknown>;
  const id = res?.data?.shipmentId ?? res?.data?.id ?? res?.shipmentId ?? res?.id;
  return id === null || id === undefined || id === '' ? undefined : String(id);
}

/**
 * Publicación de una solicitud confirmada por el backend.
 *
 * Llamar SOLO después de que la creación haya respondido OK. No al empezar la
 * solicitud, no al hacer clic en Publicar, no si queda en borrador, no ante error.
 *
 * Emite por dataLayer.push({ event }), que es lo que consume el activador de evento
 * personalizado `CE - transport_request_published` del contenedor GTM-59L6VSLB. Ese
 * activador dispara la etiqueta `GA4 - Solicitud publicada`, que manda el evento a
 * G-VQMBH7NSEM con el nombre `manual_event_SUBMIT_LEAD_FORM` — ya registrado como
 * evento clave en GA4 y ya importado a Ads como acción de conversión.
 *
 * No se usa gtag() para esto. Medido el 2026-09-16 contra nexvia.io: con el destino
 * G-VQMBH7NSEM ya inicializado por el contenedor, no se pudo montar un segundo contexto
 * gtag de página para ese mismo destino — ni sobre el mismo dataLayer, ni con uno propio
 * (&l=), ni por el alias AW-. Es un hecho sobre ESE destino ya tomado, no sobre la API de
 * gtag en general: en esta misma app gtag() funciona para G-DLE829Z7SD, que carga por
 * código y no lo administra ningún contenedor.
 */
export function trackRequestPublished(requestId: string | undefined, shipmentType: ShipmentType | null): void {
  // Todo el cuerpo va dentro del try, incluida la lectura de la cookie. Medir no puede
  // tener permiso de romper el negocio: si esto propaga, la excepción sube al catch de
  // handleSubmit, el usuario ve "Error al crear la solicitud" con el envío YA creado en el
  // backend, no se limpia pendingShipment y no se navega — y lo natural es que reintente y
  // cree un segundo envío. `document.cookie` tira SecurityError en un iframe sandboxeado o
  // con la site data bloqueada, así que no es hipotético.
  try {
    if (typeof window === 'undefined') return;

    if (!requestId) {
      // Se dispara igual: la publicación ocurrió de verdad y perder la conversión por no
      // poder leer un campo es peor que un evento sin request_id. Pero queda registrado,
      // porque significa que el backend cambió la forma de su respuesta.
      captureMessage('[analytics] publicación sin request_id resoluble', 'warning');
    }

    window.dataLayer = window.dataLayer || [];
    // The push is shaped as { request_id, firstTouch?, lastTouch?, shipment_type, event }.
    // In GTM, data layer variables read those paths with dot notation: `firstTouch.gclid` and `lastTouch.utmSource`.
    //
    // `event` va al final a propósito. La atribución sale de una cookie sobre .nexvia.io, que
    // puede escribir cualquier script de cualquier subdominio: si el spread fuera después,
    // una cookie con {"event": "..."} renombraría el evento y ese navegador dejaría de
    // generar conversiones durante 90 días, sin error en ningún lado. readAttribution()
    // además filtra por lista blanca, así que esto es la segunda barrera.
    window.dataLayer.push({
      request_id: requestId,
      ...readAttribution(),
      shipment_type: shipmentType ?? undefined,
      event: EVENT_NAME
    });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[analytics] trackRequestPublished falló', e);
  }
}
